import React, { useState, useEffect, useRef, useCallback } from 'react';
import { User, ChevronDown, X } from 'lucide-react';
import api from '../../services/api';

/**
 * UserSelectDropdown - Searchable dropdown for selecting firm members
 *
 * Props:
 * - value: string | null - the selected user ID
 * - onChange: (userId: string | null, user: object | null) => void - callback when selection changes
 * - placeholder: string - placeholder text for the input
 * - className: string - additional classes for the container
 * - disabled: boolean - whether the input is disabled
 * - includeSelf: boolean - include current user in results (for task assignment, default true)
 */
const UserSelectDropdown = ({
  value,
  onChange,
  placeholder = "Search for a user...",
  className = "",
  disabled = false,
  includeSelf = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch users when search query changes
  useEffect(() => {
    if (!isOpen) return;

    const fetchUsers = async () => {
      setLoading(true);
      try {
        const response = await api.get('/users/firm-users/autocomplete', {
          params: { query: searchQuery, limit: 10, include_self: includeSelf }
        });
        setUsers(response.data || []);
        setSelectedIndex(0);
      } catch (error) {
        console.error('Error fetching users:', error);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(fetchUsers, 150);
    return () => clearTimeout(debounceTimer);
  }, [isOpen, searchQuery, includeSelf]);

  // Fetch selected user details when value changes externally
  useEffect(() => {
    if (value && !selectedUser) {
      // If we have a value but no selected user data, try to fetch it
      const fetchUserDetails = async () => {
        try {
          const response = await api.get('/users/firm-users/autocomplete', {
            params: { query: '', limit: 50, include_self: includeSelf }
          });
          const user = (response.data || []).find(u => u.id === value);
          if (user) {
            setSelectedUser(user);
          }
        } catch (error) {
          console.error('Error fetching user details:', error);
        }
      };
      fetchUserDetails();
    } else if (!value) {
      setSelectedUser(null);
    }
  }, [value, selectedUser, includeSelf]);

  const handleSelect = useCallback((user) => {
    setSelectedUser(user);
    setIsOpen(false);
    setSearchQuery('');
    onChange(user.id, user);
  }, [onChange]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, users.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (users[selectedIndex]) {
          handleSelect(users[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchQuery('');
        break;
      case 'Tab':
        setIsOpen(false);
        break;
      default:
        break;
    }
  }, [isOpen, users, selectedIndex, handleSelect]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedUser(null);
    setSearchQuery('');
    onChange(null, null);
  };

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(true);
      inputRef.current?.focus();
    }
  };

  const handleInputChange = (e) => {
    setSearchQuery(e.target.value);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Selected user display / Input field */}
      <div
        onClick={handleInputClick}
        className={`w-full border border-gray-300 rounded-md px-3 py-2 flex items-center gap-2 cursor-pointer bg-white
          ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'hover:border-gray-400'}
          ${isOpen ? 'ring-2 ring-yellow-500 border-transparent' : ''}`}
      >
        {selectedUser && !isOpen ? (
          <>
            {selectedUser.avatar_url ? (
              <img
                src={selectedUser.avatar_url}
                alt={selectedUser.display_name}
                className="w-6 h-6 rounded-full"
              />
            ) : (
              <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                <User className="w-4 h-4 text-gray-500" />
              </div>
            )}
            <span className="flex-1 text-gray-900 truncate">{selectedUser.display_name}</span>
            {!disabled && (
              <button
                type="button"
                onClick={handleClear}
                aria-label="Clear selection"
                className="p-0.5 hover:bg-gray-100 rounded"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
          </>
        ) : (
          <>
            <User className="w-5 h-5 text-gray-400 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              role="combobox"
              aria-expanded={isOpen}
              aria-controls="user-select-listbox"
              aria-haspopup="listbox"
              aria-autocomplete="list"
              aria-activedescendant={isOpen && users[selectedIndex] ? `user-option-${users[selectedIndex].id}` : undefined}
              value={searchQuery}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={disabled}
              className="flex-1 outline-none focus:outline-none focus:ring-0 border-none bg-transparent text-gray-900 placeholder-gray-400"
            />
            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div id="user-select-listbox" role="listbox" className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-[240px] overflow-y-auto">
          {loading ? (
            <div className="px-3 py-2 text-sm text-gray-500">
              Searching...
            </div>
          ) : users.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">
              {searchQuery ? 'No users found' : 'Type to search or scroll to browse'}
            </div>
          ) : (
            users.map((user, index) => (
              <button
                key={user.id}
                id={`user-option-${user.id}`}
                role="option"
                aria-selected={index === selectedIndex}
                type="button"
                onClick={() => handleSelect(user)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-100 ${
                  index === selectedIndex ? 'bg-yellow-50' : ''
                }`}
              >
                {user.avatar_url ? (
                  <img
                    src={user.avatar_url}
                    alt={user.display_name}
                    className="w-8 h-8 rounded-full"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-gray-500" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {user.display_name}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {user.email}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default UserSelectDropdown;
